/**
 * Read-only "Controller" card for the Apex dashboard.
 *
 * Surfaces the richer telemetry that the modern AOS REST transport
 * exposes (and the legacy CGI transport does not): controller identity
 * (model / firmware / hardware / serial), live feed-cycle status, and
 * the list of attached AquaBus modules. Everything here is display-only
 * — this component never writes to the controller.
 *
 * When the agent is talking to a legacy controller (no `system`, `feed`,
 * or `modules`) the card renders nothing so the dashboard stays clean.
 */
import { Badge } from './Badge';
import { Card, CardBody, CardHeader, CardTitle } from './Card';
import type {
  ApexFeedStatus,
  ApexGeneration,
  ApexModuleStatus,
  ApexSystemInfo,
} from '../lib/types';

export interface ApexControllerInfoProps {
  generation?: ApexGeneration;
  system?: ApexSystemInfo | null;
  feed?: ApexFeedStatus | null;
  modules?: ApexModuleStatus[];
}

/** True when at least one system identity field is populated. */
function hasSystemInfo(system: ApexSystemInfo | null | undefined): system is ApexSystemInfo {
  if (!system) return false;
  return Boolean(system.software || system.hardware || system.type || system.serial);
}

function FeedBadge({ feed }: { feed: ApexFeedStatus }) {
  if (feed.active) {
    return (
      <Badge variant="warn" live>
        {feed.cycle ? `Feeding · Cycle ${feed.cycle}` : 'Feeding'}
      </Badge>
    );
  }
  return <Badge variant="neutral">Feed idle</Badge>;
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs uppercase tracking-wider text-reef-400">{label}</span>
      <span className="text-sm text-reef-100 tabular-nums">{value}</span>
    </div>
  );
}

export function ApexControllerInfo({
  generation,
  system,
  feed,
  modules,
}: ApexControllerInfoProps) {
  const systemInfo = hasSystemInfo(system) ? system : null;
  const moduleList = modules ?? [];
  // Legacy controllers report none of these — render nothing rather than
  // an empty shell so the dashboard layout is unchanged for them.
  if (!systemInfo && !feed && moduleList.length === 0) {
    return null;
  }

  return (
    <Card data-testid="apex-controller-info">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle>Controller</CardTitle>
          <div className="flex items-center gap-2">
            {feed ? <FeedBadge feed={feed} /> : null}
            {generation ? (
              <Badge variant={generation === 'modern' ? 'info' : 'neutral'}>
                {generation === 'modern' ? 'REST' : 'legacy'}
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {systemInfo ? (
          <div className="grid gap-1.5 sm:grid-cols-2">
            <InfoRow label="Model" value={systemInfo.type} />
            <InfoRow label="Firmware" value={systemInfo.software} />
            <InfoRow label="Hardware" value={systemInfo.hardware} />
            <InfoRow label="Serial" value={systemInfo.serial} />
          </div>
        ) : null}

        {moduleList.length > 0 ? (
          <div className="mt-4" data-testid="apex-modules">
            <h3 className="mb-2 text-xs uppercase tracking-wider text-reef-400">
              Modules ({moduleList.length})
            </h3>
            <ul className="grid gap-1.5">
              {moduleList.map((mod) => (
                <li
                  key={mod.key}
                  className="flex items-center justify-between gap-3 rounded-md border border-reef-700/40 bg-reef-800/30 px-2.5 py-1.5"
                  data-testid="apex-module-row"
                >
                  <span className="text-sm text-reef-100">
                    {mod.name ?? mod.hwType ?? mod.key}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-reef-400 tabular-nums">
                    {mod.hwType && mod.name ? <span>{mod.hwType}</span> : null}
                    {mod.swrev ? <span>v{mod.swrev}</span> : null}
                    {mod.present === false ? (
                      <Badge variant="danger">offline</Badge>
                    ) : mod.present === true ? (
                      <Badge variant="ok">present</Badge>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
